import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const SecondaryNavbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isClimateImpactsOpen, setIsClimateImpactsOpen] = useState(false);

  const climateRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        climateRef.current &&
        !climateRef.current.contains(event.target as Node)
      ) {
        setIsClimateImpactsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNavigation = (direction: "prev" | "next") => {
    const paths = [
      "/dashboard/overview",
      "/dashboard/profile",
      "/dashboard/stressors",
      "/dashboard/risk-indices",
      "/dashboard/impacts",
      "/dashboard/economics",
      "/dashboard/adaptation-options",
    ];

    const currentIndex = paths.indexOf(location.pathname);
    let newIndex: number;

    if (direction === "prev") {
      newIndex = currentIndex > 0 ? currentIndex - 1 : paths.length - 1;
    } else {
      newIndex = currentIndex < paths.length - 1 ? currentIndex + 1 : 0;
    }

    navigate(paths[newIndex]);
    setIsMenuOpen(false);
  };

  const getCurrentTitle = () => {
    switch (location.pathname) {
      case "/dashboard/overview":
        return "OVERVIEW";
      case "/dashboard/profile":
        return "CLIMATE PROFILE";
      case "/dashboard/stressors":
        return "CLIMATE STRESSORS";
      case "/dashboard/risk-indices":
        return "CLIMATE RISK INDEX";
      case "/dashboard/adaptation-options":
        return "ADAPTATION OPTIONS";
      case "/dashboard/impacts":
        return "CLIMATE IMPACT";
      case "/dashboard/economics":
        return "CLIMATE IMPACT";
      default:
        return "";
    }
  };

  return (
    <nav
      className="bg-green-200 text-white border-t border-green-800 relative z-40"
      style={{ overflow: "visible" }}
    >
      <div className="container max-w-7xl mx-auto px-4">
        {/* Version Desktop */}
        <div className="hidden md:flex items-center h-12">
          {/* Chevrons de navigation */}
          <div className="flex items-center space-x-2 mr-4">
            <button
              onClick={() => handleNavigation("prev")}
              className="text-green-500 hover:text-lime-100 p-2 focus:outline-none focus:ring-1 focus:ring-green-500 rounded-full"
              aria-label="Page précédente"
            >
              <span className="text-xl">&lt;</span>
            </button>
            <button
              onClick={() => handleNavigation("next")}
              className="text-green-500 hover:text-lime-100 p-2 focus:outline-none focus:ring-1 focus:ring-green-500 rounded-full"
              aria-label="Page suivante"
            >
              <span className="text-xl">&gt;</span>
            </button>
          </div>

          {/* Links Desktop avec distribution équitable de l'espace */}
          <div className="flex items-stretch flex-grow mx-2 lg:mx-4 h-full">
            <Link
              to="/dashboard/overview"
              className={`px-2 lg:px-4 py-3 text-sm transition-colors duration-200 whitespace-nowrap h-full flex items-center justify-center flex-1
                ${
                  location.pathname === "/dashboard/overview"
                    ? "bg-white text-green-200"
                    : "text-white hover:text-lime-100"
                }`}
            >
              OVERVIEW
            </Link>

            <Link
              to="/dashboard/profile"
              className={`px-2 lg:px-4 py-3 text-sm transition-colors duration-200 whitespace-nowrap h-full flex items-center justify-center flex-1
                ${
                  location.pathname === "/dashboard/profile"
                    ? "bg-white text-green-200"
                    : "text-white hover:text-lime-100"
                }`}
            >
              CLIMATE PROFILE
            </Link>

            <Link
              to="/dashboard/stressors"
              className={`px-2 lg:px-4 py-3 text-sm transition-colors duration-200 whitespace-nowrap h-full flex items-center justify-center flex-1
                ${
                  location.pathname === "/dashboard/stressors"
                    ? "bg-white text-green-200"
                    : "text-white hover:text-lime-100"
                }`}
            >
              CLIMATE STRESSORS
            </Link>

            <Link
              to="/dashboard/risk-indices"
              className={`px-2 lg:px-4 py-3 text-sm transition-colors duration-200 whitespace-nowrap h-full flex items-center justify-center flex-1
                ${
                  location.pathname === "/dashboard/risk-indices"
                    ? "bg-white text-green-200"
                    : "text-white hover:text-lime-100"
                }`}
            >
              CLIMATE RISK INDEX
            </Link>

            <div className="relative min-h-full flex-1" ref={climateRef}>
              <button
                onClick={() => setIsClimateImpactsOpen(!isClimateImpactsOpen)}
                className={`px-2 lg:px-4 py-3 text-sm transition-colors duration-200 whitespace-nowrap h-full flex items-center justify-center flex-1 space-x-1
                  ${
                    location.pathname === "/dashboard/impacts" ||
                    location.pathname === "/dashboard/economics"
                      ? "bg-white text-green-200"
                      : "text-white hover:text-lime-100"
                  }`}
              >
                <span>CLIMATE IMPACT</span>
                <ChevronDown
                  size={16}
                  className={`transition-transform duration-200 ${
                    isClimateImpactsOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {isClimateImpactsOpen && (
                <div className="absolute top-full left-0 bg-white shadow-lg border border-gray-200 rounded-md min-w-48 z-[9999]">
                  <Link
                    to="/dashboard/impacts"
                    onClick={() => setIsClimateImpactsOpen(false)}
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  >
                    CROPS PRODUCTION
                  </Link>
                  <Link
                    to="/dashboard/economics"
                    onClick={() => setIsClimateImpactsOpen(false)}
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  >
                    ECONOMICS
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/dashboard/adaptation-options"
              className={`px-2 lg:px-4 py-3 text-sm transition-colors duration-200 whitespace-nowrap h-full flex items-center justify-center flex-1
                ${
                  location.pathname === "/dashboard/adaptation-options"
                    ? "bg-white text-green-200"
                    : "text-white hover:text-lime-100"
                }`}
            >
              ADAPTATION OPTIONS
            </Link>
          </div>

          {/* Search box Desktop */}
          <div className="ml-2 lg:ml-4 flex-shrink-0">
            <input
              type="search"
              placeholder="Search..."
              className="px-3 py-1 rounded-full text-sm bg-white border border-green-800 text-white placeholder-green-400 focus:outline-none focus:ring-1 focus:ring-green-500 w-32 lg:w-48"
            />
          </div>
        </div>

        {/* Version Mobile - reste inchangée */}
        <div className="md:hidden">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleNavigation("prev")}
                className="text-green-500 hover:text-green-400 p-2"
                aria-label="Page précédente"
              >
                <span className="text-xl">&lt;</span>
              </button>
              <button
                onClick={() => handleNavigation("next")}
                className="text-green-500 hover:text-green-400 p-2"
                aria-label="Page suivante"
              >
                <span className="text-xl">&gt;</span>
              </button>
            </div>

            <div className="flex-grow text-center text-sm">
              {getCurrentTitle()}
            </div>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 text-green-500 hover:text-green-400"
              aria-label="Menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {isMenuOpen && (
            <>
              <div
                className="fixed inset-0 bg-black bg-opacity-50 z-40"
                onClick={() => setIsMenuOpen(false)}
              />
              <div className="absolute left-0 right-0 bg-green-950/95 backdrop-blur-sm border-t border-green-800 z-50 shadow-lg">
                <div className="flex flex-col px-4 py-2">
                  <Link
                    to="/dashboard/overview"
                    onClick={() => setIsMenuOpen(false)}
                    className={`py-3 text-sm border-b border-green-800/50
                      ${
                        location.pathname === "/dashboard/overview"
                          ? "text-white font-semibold"
                          : "text-white/90 hover:text-white"
                      }`}
                  >
                    OVERVIEW
                  </Link>

                  <Link
                    to="/dashboard/profile"
                    onClick={() => setIsMenuOpen(false)}
                    className={`py-3 text-sm border-b border-green-800/50
                      ${
                        location.pathname === "/dashboard/profile"
                          ? "text-white font-semibold"
                          : "text-white/90 hover:text-white"
                      }`}
                  >
                    CLIMATE PROFILE
                  </Link>

                  <Link
                    to="/dashboard/stressors"
                    onClick={() => setIsMenuOpen(false)}
                    className={`py-3 text-sm border-b border-green-800/50
                      ${
                        location.pathname === "/dashboard/stressors"
                          ? "text-white font-semibold"
                          : "text-white/90 hover:text-white"
                      }`}
                  >
                    CLIMATE STRESSORS
                  </Link>

                  <Link
                    to="/dashboard/risk-indices"
                    onClick={() => setIsMenuOpen(false)}
                    className={`py-3 text-sm border-b border-green-800/50
                      ${
                        location.pathname === "/dashboard/risk-indices"
                          ? "text-white font-semibold"
                          : "text-white/90 hover:text-white"
                      }`}
                  >
                    CLIMATE RISK INDEX
                  </Link>

                  <div className="border-b border-green-800/50">
                    <button
                      onClick={() =>
                        setIsClimateImpactsOpen(!isClimateImpactsOpen)
                      }
                      className={`w-full py-3 text-sm flex items-center justify-between
                        ${
                          location.pathname === "/dashboard/impacts" ||
                          location.pathname === "/dashboard/economics"
                            ? "text-white font-semibold"
                            : "text-white/90 hover:text-white"
                        }`}
                    >
                      <span>CLIMATE IMPACT</span>
                      <ChevronDown
                        size={16}
                        className={`transition-transform duration-200 ${
                          isClimateImpactsOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {isClimateImpactsOpen && (
                      <div className="pl-4 pb-2">
                        <Link
                          to="/dashboard/impacts"
                          onClick={() => {
                            setIsMenuOpen(false);
                            setIsClimateImpactsOpen(false);
                          }}
                          className="block py-2 text-sm text-white/80 hover:text-white"
                        >
                          Crops production
                        </Link>
                        <Link
                          to="/dashboard/economics"
                          onClick={() => {
                            setIsMenuOpen(false);
                            setIsClimateImpactsOpen(false);
                          }}
                          className="block py-2 text-sm text-white/80 hover:text-white"
                        >
                          Economics
                        </Link>
                      </div>
                    )}
                  </div>

                  <Link
                    to="/dashboard/adaptation-options"
                    onClick={() => setIsMenuOpen(false)}
                    className={`py-3 text-sm border-b border-green-800/50
                      ${
                        location.pathname === "/dashboard/adaptation-options"
                          ? "text-white font-semibold"
                          : "text-white/90 hover:text-white"
                      }`}
                  >
                    Adaptation Options
                  </Link>
                </div>

                <div className="p-4 border-t border-green-800/50">
                  <input
                    type="search"
                    placeholder="Search..."
                    className="w-full px-3 py-2 rounded-full text-sm bg-white/10 border border-green-800/50 text-white placeholder-green-400 focus:outline-none focus:ring-1 focus:ring-green-500"
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default SecondaryNavbar;
